import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom'

import EastIcon from '@mui/icons-material/East'
import {Box, Divider, Stack, Typography} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'

import {BackButton} from '@progyny/components/Buttons'
import ContactSupport from '@progyny/components/ContactSupport'
import PageLoader from '@progyny/components/PageLoader'
import {
  useAddPartnerMutation,
  useMeQuery,
  useResendPartnerConsentMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import PAForm from '@progyny/pages/Budget/PartnerAgreement/PAForm'
import {FormValues} from '@progyny/pages/Budget/PartnerAgreement/PAFormModel'
import {
  AvatarWithOnlyCheckmark,
  AvatarWithOnlyWaitingIcon,
  AvatarWithRestart,
} from '@progyny/pages/Budget/PartnerAgreement/helpers/Avatars'
import {SubpageOneColGrid} from '@progyny/pages/Profile/fragments/grid'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

type LocationState = {
  fromProfile?: boolean | false
  fromBudget?: boolean | false
  returnTo?: string | undefined
}

type PartnerAgreementProps = RouteComponentProps & {
  onPAFormBackBtn?: () => void
}

const PartnerAgreement: React.FC<PartnerAgreementProps> = (props) => {
  const {t} = useTranslation()
  const [isSuccess, setSuccess] = useState(false)
  const [isEmailResent, setEmailResent] = useState(false)
  const {refetch, loading: userLoading} = useMeQuery()
  const user = useMeStore((s) => s.user)
  const getErrorMessage = useErrorCodes()
  const {getPath} = useRoutes()
  const [hasRefetched, setHasRefetched] = useState(false)
  const location = useLocation<LocationState>()
  const passedState = location?.state
  const history = useHistory()
  const [resendPartnerAgreement, {loading: isResendPALoading}] =
    useResendPartnerConsentMutation()
  const parentRoute = props.match.url
    .split('/')
    .filter(Boolean)
    .slice(0, -1)
    .join('/')

  const isSimpleRoute = props.match.url === '/partner-agreement'

  const routeTo = isSimpleRoute
    ? `/budget/reimbursement`
    : `/${parentRoute}/treatment`
  const [addPartner, {loading}] = useAddPartnerMutation()
  const {enqueueSnackbar} = useSnackbar()

  useEffect(() => {
    refetch().finally(() => {
      setHasRefetched(true)
    })
  }, [refetch])

  function handleSubmit(values: FormValues, selectedPartnerId: File) {
    addPartner({
      errorPolicy: 'all',
      variables: {
        memberCity: values?.city,
        memberCountry: values?.country,
        memberPostcode: values?.postalCode,
        memberStreetAddress: values?.homeAddress,
        partnerDateOfBirth: values?.partnerDob,
        partnerEmail: values?.partnerEmail,
        partnerFirstName: values?.partnerFname,
        partnerLastName: values?.partnerLname,
        sameEmployer: values?.partnerCompany === 'yes',
        sameHousehold: values?.isSameAddressForPartner === 'yes',
        idDocument: selectedPartnerId,
        partnerEmployeeNumber: values?.partnerEnumber,
        partnerCity: values?.partnerCity,
        partnerCountry: values?.partnerCountry,
        partnerPostcode: values?.partnerPostalCode,
        docType: values?.documentType,
        partnerStreetAddress: values?.partnerHomeAddress,
      },
    })
      .then((res) => {
        if (res.data?.addPartner?.success) {
          setSuccess(true)
          refetch()
        } else if (res.data?.addPartner?.errors?.length) {
          const errors = res.data?.addPartner?.errors
          const message = errors[0]?.messages[0]
          return enqueueSnackbar({
            message: message ?? t('error_codes_unknown_error'),
            variant: 'error',
          })
        } else if (res?.errors?.length) {
          const message = res?.errors[0]?.message
          return enqueueSnackbar({
            message: message ?? t('error_codes_unknown_error'),
            variant: 'error',
          })
        }
      })
      .catch((error) => console.error(error))
  }

  const navigateToTreatment = () => {
    return history.push({
      pathname: routeTo,
      state: {from: location.pathname},
    })
  }

  function handleResendEmail() {
    resendPartnerAgreement()
      .then((res) => {
        if (res.data?.resendPartnerConsentEmail?.success) {
          setEmailResent(true)
          setSuccess(true)
          refetch()
        } else if (res.data?.resendPartnerConsentEmail?.errors) {
          const errors = res.data?.resendPartnerConsentEmail?.errors
          const message = errors[0]?.messages[0]
          return enqueueSnackbar({
            message: getErrorMessage(message as string),
            variant: 'error',
          })
        }
      })
      .catch((error) => console.error(error))
  }

  if (loading || isResendPALoading || userLoading) {
    return (
      <Box minHeight={140}>
        <PageLoader position={'center'} />
      </Box>
    )
  }

  const isUserLoaded =
    hasRefetched && user?.member?.partnerConsentStatus !== undefined

  if (!isUserLoaded) return null

  const isReview = !!(
    user.member.partnerDetails &&
    (user.member.partnerConsentStatus === 'not_done' ||
      user.member.partnerConsentStatus === 'accepted')
  )

  if (passedState?.fromProfile) {
    return (
      <Box mt={15}>
        <SubpageOneColGrid title={t('profile_partner_information')} lgSize={8}>
          <PAForm
            isReview={isReview}
            onBackBtn={props.onPAFormBackBtn}
            onPASubmit={handleSubmit}
            fromProfile={passedState?.fromProfile}
          />
        </SubpageOneColGrid>
      </Box>
    )
  }

  return (
    <Stack padding={isSimpleRoute ? 4 : 0} width='100%'>
      <Box mt={isSimpleRoute ? 9 : 0}>
        <BackButton
          text={t('common_back')}
          onBackClick={() =>
            passedState?.fromBudget
              ? history.push(`/budget`)
              : passedState?.returnTo
                ? history.push(passedState?.returnTo)
                : history.push(
                    `${getPath(RouteEnum.BUDGET)}/reimbursement/claim`
                  )
          }
        />
      </Box>
      {!isSuccess ? (
        !!user?.member?.partnerDetails ? (
          user?.member?.partnerConsentStatus === 'not_done' ? (
            <Stack
              justifyContent={'center'}
              mt={12}
              mb={4}
              px={'6rem'}
              alignItems={'center'}
            >
              <AvatarWithOnlyWaitingIcon />
              <Typography
                textAlign={'center'}
                fontSize={24}
                mb={2}
                width={'60%'}
                fontWeight={600}
              >
                {t('budget_partner_agreement_waiting_for_partner_s_approval')}
              </Typography>
              <Typography
                fontSize={18}
                fontWeight={400}
                width={'45%'}
                textAlign={'center'}
              >
                <Trans i18nKey='budget_partner_agreement_we_have_sent_partner_agreement' />
              </Typography>
              <Stack
                mt={7}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={2}
              >
                <PGButton
                  onClick={handleResendEmail}
                  label={t('budget_partner_agreement_resend_partner_agreement')}
                />
              </Stack>
              <Typography
                fontSize={18}
                fontWeight={400}
                mt={10}
                textAlign={'center'}
                width={'45%'}
                pt={5}
              >
                <Trans i18nKey='budget_partner_agreement_has_something_changed_since_last' />
              </Typography>
              <ContactSupport
                textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
                my={1}
                px={'6rem'}
                alignItems={'center'}
              />
              <Divider sx={{width: '100%', pb: 3, mb: 3}} />
            </Stack>
          ) : user?.member?.partnerConsentStatus === 'accepted' ? (
            <Box
              display='flex'
              flexDirection={passedState?.fromBudget ? 'row' : 'column'}
              alignItems={passedState?.fromBudget ? 'baseline' : 'center'}
              justifyContent={
                passedState?.fromBudget ? 'space-between' : 'center'
              }
              mt={3}
              mb={4}
              px='6rem'
            >
              <Box flex={1} display='flex' justifyContent='center' mb={6}>
                <Typography
                  textAlign='center'
                  fontSize={24}
                  fontWeight={600}
                  width={passedState?.fromBudget ? '50%' : '60%'}
                >
                  {passedState?.fromBudget
                    ? t(
                        'budget_partner_agreement_partner_agreement_is_approved_please'
                      )
                    : t(
                        'budget_partner_agreement_we_will_proceed_with_reimbursement'
                      )}
                </Typography>
              </Box>

              <PGButton
                variant={passedState?.fromBudget ? 'outlined' : 'contained'}
                endIcon={<EastIcon />}
                onClick={navigateToTreatment}
                label={t('navigation_start_claim_request')}
              />

              {!passedState?.fromBudget && (
                <>
                  <Typography
                    fontSize={18}
                    fontWeight={400}
                    width={'45%'}
                    textAlign={'center'}
                    mt={6}
                  >
                    <Trans i18nKey='budget_partner_agreement_has_something_changed_since_last' />
                  </Typography>
                  <ContactSupport
                    textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
                    my={1}
                    px={'6rem'}
                    alignItems={'center'}
                  />
                </>
              )}
            </Box>
          ) : (
            <Stack
              justifyContent={'center'}
              mt={12}
              mb={4}
              px={'6rem'}
              alignItems={'center'}
            >
              <AvatarWithOnlyWaitingIcon />
              <Typography
                textAlign={'center'}
                fontSize={24}
                mb={2}
                width={'60%'}
                fontWeight={600}
              >
                {t('budget_partner_agreement_partner_agreement_declined')}
              </Typography>
              <Typography
                fontSize={18}
                fontWeight={400}
                width={'45%'}
                textAlign={'center'}
              >
                <Trans i18nKey='budget_partner_agreement_we_regret_to_inform_you' />
              </Typography>
              <Typography
                fontSize={18}
                mt={2}
                fontWeight={400}
                width={'45%'}
                textAlign={'center'}
              >
                <Trans i18nKey='budget_partner_agreement_you_can_discuss_matter_further' />
              </Typography>
              <Box mt={7}>
                <PGButton
                  onClick={handleResendEmail}
                  endIcon={<AvatarWithRestart />}
                  label={t('budget_partner_agreement_restart_process')}
                />
              </Box>
              <Divider sx={{width: '100%', pb: 3, mb: 3}} />
            </Stack>
          )
        ) : (
          <Stack alignItems={'center'} gap={2} mt={12} mb={10}>
            <Typography fontSize={24} fontWeight={600}>
              {t('budget_partner_agreement_let_s_set_up_your')}
            </Typography>
            <Typography
              fontSize={18}
              fontWeight={400}
              textAlign={'center'}
              width={'35%'}
            >
              <Trans i18nKey='budget_partner_agreement_we_need_to_know_more' />
            </Typography>
          </Stack>
        )
      ) : isEmailResent ? (
        <Stack justifyContent={'center'} mt={12} mb={4} alignItems={'center'}>
          <AvatarWithOnlyCheckmark />
          <Typography
            textAlign={'center'}
            fontSize={24}
            mb={2}
            width={'45%'}
            fontWeight={600}
          >
            {t('budget_partner_agreement_partner_agreement_resent')}
          </Typography>
          <Typography
            fontSize={18}
            fontWeight={400}
            width={'45%'}
            textAlign={'center'}
          >
            <Trans i18nKey='budget_partner_agreement_we_ve_just_resent_agreement' />
          </Typography>
          <Typography
            fontSize={18}
            fontWeight={400}
            my={2}
            width={'45%'}
            textAlign={'center'}
          >
            <Trans i18nKey='budget_partner_agreement_please_ask_them_to_check' />
          </Typography>
        </Stack>
      ) : (
        <Stack justifyContent={'center'} mt={12} mb={4} alignItems={'center'}>
          <AvatarWithOnlyCheckmark />
          <Typography
            textAlign={'center'}
            fontSize={24}
            mb={2}
            width={'60%'}
            fontWeight={600}
          >
            {t('budget_partner_agreement_partner_agreement_has_been_sent')}
          </Typography>
          <Typography
            fontSize={18}
            fontWeight={400}
            width={'60%'}
            textAlign={'center'}
          >
            <Trans i18nKey='budget_partner_agreement_we_will_notify_you_via' />
          </Typography>
        </Stack>
      )}
      {!isSuccess && (
        <PAForm
          isReview={isReview}
          onBackBtn={props.onPAFormBackBtn}
          onPASubmit={handleSubmit}
        />
      )}
      {isSuccess && (
        <ContactSupport
          textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
          mt={16}
          my={3}
          px={'6rem'}
          alignItems={'center'}
        />
      )}
    </Stack>
  )
}

export default PartnerAgreement
